<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AiAgentsAPI from '../../../../../api/aiAgents';

const props = defineProps({
  agent: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const documents = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const editingDocumentId = ref(null);
const selectedFile = ref(null);
const fileError = ref('');
const isDragging = ref(false);
const fileInput = ref(null);
const newDocument = ref({
  name: '',
  source_type: 'text',
  content: '',
  source_url: '',
});

const defaultDocument = {
  name: '',
  source_type: 'text',
  content: '',
  source_url: '',
};

const industryForm = ref({
  specialization: '',
  businessName: '',
  website: '',
  description: '',
});

const defaultIndustry = {
  specialization: '',
  businessName: '',
  website: '',
  description: '',
};

const buildIndustryContent = () => {
  const i = industryForm.value;
  const lines = [];
  if (i.specialization) lines.push(`Especialización: ${i.specialization}`);
  if (i.businessName) lines.push(`Nombre del negocio: ${i.businessName}`);
  if (i.website) lines.push(`Sitio web del negocio: ${i.website}`);
  if (i.description) lines.push(`Descripción del negocio: ${i.description}`);
  return lines.join('\n');
};

const defaultFaqItem = { question: '', answer: '' };
const faqList = ref([{ ...defaultFaqItem }]);

const buildFaqContent = () =>
  faqList.value
    .filter(item => item.question.trim() || item.answer.trim())
    .map(
      (item, index) =>
        `Pregunta ${index + 1}: ${item.question}\nRespuesta: ${item.answer}`
    )
    .join('\n\n');

const addFaq = () => faqList.value.push({ ...defaultFaqItem });
const removeFaq = index => faqList.value.splice(index, 1);

const parseFaqContent = content => {
  if (!content) return [{ ...defaultFaqItem }];
  const items = [];
  content.split(/\n\n+/).forEach(block => {
    const lines = block.split('\n').filter(line => line.trim());
    const qMatch = lines[0]?.match(/^Pregunta \d+:\s*(.*)$/);
    const aMatch = lines[1]?.match(/^Respuesta:\s*(.*)$/);
    const question = qMatch ? qMatch[1] : lines[0] || '';
    const answer = aMatch ? aMatch[1] : lines[1] || '';
    if (question || answer) items.push({ question, answer });
  });
  return items.length ? items : [{ ...defaultFaqItem }];
};

const documentTypes = [
  { value: 'text', labelKey: 'TYPE_TEXT', icon: 'i-lucide-file-text' },
  { value: 'faq', labelKey: 'TYPE_FAQ', icon: 'i-lucide-message-circle' },
  { value: 'industry', labelKey: 'TYPE_INDUSTRY', icon: 'i-lucide-briefcase' },
  { value: 'url', labelKey: 'TYPE_URL', icon: 'i-lucide-link' },
  { value: 'pdf', labelKey: 'TYPE_PDF', icon: 'i-woot-file-pdf' },
  { value: 'docx', labelKey: 'TYPE_DOCX', icon: 'i-woot-file-doc' },
  { value: 'xlsx', labelKey: 'TYPE_XLSX', icon: 'i-woot-file-xls' },
];

const isTextType = type => ['text', 'faq'].includes(type);
const isFileType = type => ['pdf', 'docx', 'xlsx'].includes(type);
const isUrlType = type => type === 'url';

const loadDocuments = async () => {
  isLoading.value = true;
  try {
    const { data } = await AiAgentsAPI.getDocuments(props.agent.id);
    documents.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  loadDocuments();
});

watch(
  () => newDocument.value.source_type,
  () => {
    selectedFile.value = null;
    fileError.value = '';
    if (!isTextType(newDocument.value.source_type)) {
      newDocument.value.content = '';
    }
    if (newDocument.value.source_type !== 'faq') {
      faqList.value = [{ ...defaultFaqItem }];
    }
    if (!isUrlType(newDocument.value.source_type)) {
      newDocument.value.source_url = '';
    }
  }
);

const formTitle = computed(() =>
  editingDocumentId.value
    ? t('AI_AGENTS.KNOWLEDGE.EDIT')
    : t('AI_AGENTS.KNOWLEDGE.ADD')
);

const saveLabel = computed(() =>
  editingDocumentId.value
    ? t('AI_AGENTS.KNOWLEDGE.SAVE')
    : t('AI_AGENTS.KNOWLEDGE.CREATE')
);

const openForm = () => {
  newDocument.value = { ...defaultDocument };
  industryForm.value = { ...defaultIndustry };
  faqList.value = [{ ...defaultFaqItem }];
  editingDocumentId.value = null;
  selectedFile.value = null;
  fileError.value = '';
  showForm.value = true;
};

const openEdit = doc => {
  editingDocumentId.value = doc.id;
  newDocument.value = {
    name: doc.name,
    source_type: doc.source_type,
    content: doc.content || '',
    source_url: doc.source_url || '',
  };
  faqList.value =
    doc.source_type === 'faq'
      ? parseFaqContent(doc.content)
      : [{ ...defaultFaqItem }];
  selectedFile.value = null;
  fileError.value = '';
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  newDocument.value = { ...defaultDocument };
  industryForm.value = { ...defaultIndustry };
  faqList.value = [{ ...defaultFaqItem }];
  editingDocumentId.value = null;
  selectedFile.value = null;
  fileError.value = '';
};

const handleFile = file => {
  if (!file) return;

  const ext = file.name.split('.').pop().toLowerCase();
  const allowed = { pdf: 'pdf', docx: 'docx', xlsx: 'xlsx' };
  if (!allowed[ext]) {
    fileError.value = t('AI_AGENTS.KNOWLEDGE.FILE_TYPE_ERROR');
    selectedFile.value = null;
    return;
  }

  fileError.value = '';
  selectedFile.value = file;
  newDocument.value.source_type = allowed[ext];
  if (!newDocument.value.name) {
    newDocument.value.name = file.name.replace(/\\.[^/.]+$/, '');
  }
};

const onFileChange = event => handleFile(event.target.files?.[0]);

const onDrop = event => {
  event.preventDefault();
  isDragging.value = false;
  handleFile(event.dataTransfer?.files?.[0]);
};

const openFileInput = () => fileInput.value?.click();

const saveDocument = async () => {
  if (editingDocumentId.value) {
    const payload = { name: newDocument.value.name };
    if (isTextType(newDocument.value.source_type)) {
      payload.content =
        newDocument.value.source_type === 'faq'
          ? buildFaqContent()
          : newDocument.value.content;
    }
    if (isUrlType(newDocument.value.source_type)) {
      payload.source_url = newDocument.value.source_url;
    }
    await AiAgentsAPI.updateDocument(
      props.agent.id,
      editingDocumentId.value,
      payload
    );
  } else if (isFileType(newDocument.value.source_type)) {
    if (!selectedFile.value) return;

    const form = new FormData();
    form.append('name', newDocument.value.name);
    form.append('source_type', newDocument.value.source_type);
    form.append('source_file', selectedFile.value);
    await AiAgentsAPI.createDocument(props.agent.id, form);
  } else if (newDocument.value.source_type === 'industry') {
    const payload = { ...newDocument.value };
    payload.source_type = 'text';
    payload.content = buildIndustryContent();
    if (!payload.name.trim()) {
      payload.name = industryForm.value.businessName || 'Industria';
    }
    await AiAgentsAPI.createDocument(props.agent.id, payload);
  } else if (newDocument.value.source_type === 'faq') {
    const payload = { ...newDocument.value, content: buildFaqContent() };
    payload.name = payload.name.trim() || 'Preguntas frecuentes';
    await AiAgentsAPI.createDocument(props.agent.id, payload);
  } else {
    await AiAgentsAPI.createDocument(props.agent.id, newDocument.value);
  }

  closeForm();
  await loadDocuments();
};

const deleteDocument = async id => {
  await AiAgentsAPI.deleteDocument(props.agent.id, id);
  await loadDocuments();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
        @click="openForm"
      >
        {{ t('AI_AGENTS.KNOWLEDGE.ADD') }}
      </button>
    </div>

    <div v-if="isLoading" class="text-sm text-n-slate-10">
      {{ t('AI_AGENTS.LOADING') }}
    </div>

    <div v-else-if="!documents.length" class="text-sm text-n-slate-10">
      {{ t('AI_AGENTS.KNOWLEDGE.NO_RESULTS') }}
    </div>

    <table v-else class="w-full text-sm text-left border-collapse">
      <thead>
        <tr class="border-b border-n-slate-6 text-n-slate-10">
          <th class="py-2">{{ t('AI_AGENTS.KNOWLEDGE.NAME') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.KNOWLEDGE.TYPE') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.KNOWLEDGE.STATUS') }}</th>
          <th class="py-2">{{ t('AI_AGENTS.TABLE.ACTIONS') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="doc in documents"
          :key="doc.id"
          class="border-b border-n-slate-6"
        >
          <td class="py-3">{{ doc.name }}</td>
          <td class="py-3">{{ doc.source_type }}</td>
          <td class="py-3">{{ doc.status }}</td>
          <td class="py-3 flex gap-3">
            <button
              class="text-n-brand hover:text-n-brand/80"
              @click="openEdit(doc)"
            >
              {{ t('AI_AGENTS.TABLE.EDIT') }}
            </button>
            <button
              class="text-n-ruby-9 hover:text-n-ruby-10"
              @click="deleteDocument(doc.id)"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.DELETE') }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-if="showForm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto p-4"
      @click.self="closeForm"
    >
      <div
        class="w-full max-w-3xl max-h-[85vh] overflow-y-auto p-8 rounded-xl bg-n-surface-1 shadow-xl my-auto"
      >
        <div class="mb-6">
          <span class="text-sm font-medium text-n-slate-11 mb-2 block">
            {{ t('AI_AGENTS.KNOWLEDGE.TYPE') }}
          </span>
          <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3">
            <button
              v-for="type in documentTypes"
              :key="type.value"
              type="button"
              :disabled="Boolean(editingDocumentId)"
              class="flex flex-col items-center justify-center gap-2 px-3 py-4 text-sm font-medium rounded-xl border border-n-slate-6 bg-n-surface-2 text-n-slate-12 transition-colors"
              :class="{
                'bg-n-brand/10 !text-n-brand border-n-brand':
                  newDocument.source_type === type.value,
                'hover:border-n-brand hover:text-n-brand':
                  newDocument.source_type !== type.value,
                'opacity-60 cursor-not-allowed': editingDocumentId,
              }"
              @click="newDocument.source_type = type.value"
            >
              <Icon :icon="type.icon" class="w-6 h-6" />
              {{ t(`AI_AGENTS.KNOWLEDGE.${type.labelKey}`) }}
            </button>
          </div>
        </div>

        <h3 class="mb-4 text-xl font-semibold">
          {{ formTitle }}
        </h3>

        <div class="grid gap-5">
          <label class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.NAME')
            }}</span>
            <input
              v-model="newDocument.name"
              type="text"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>

          <div v-if="newDocument.source_type === 'faq'" class="grid gap-4">
            <div
              v-for="(item, index) in faqList"
              :key="index"
              class="grid gap-3 p-4 rounded-lg border border-n-slate-6 bg-n-surface-2"
            >
              <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-n-slate-11">
                  {{
                    t('AI_AGENTS.KNOWLEDGE.QUESTION_NUMBER', {
                      number: index + 1,
                    })
                  }}
                </span>
                <button
                  v-if="faqList.length > 1"
                  type="button"
                  class="text-xs text-n-ruby-9 hover:text-n-ruby-10"
                  @click="removeFaq(index)"
                >
                  {{ t('AI_AGENTS.KNOWLEDGE.REMOVE_QUESTION') }}
                </button>
              </div>
              <label class="grid gap-1">
                <span class="text-sm font-medium">{{
                  t('AI_AGENTS.KNOWLEDGE.QUESTION')
                }}</span>
                <input
                  v-model="item.question"
                  type="text"
                  class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                  :placeholder="t('AI_AGENTS.KNOWLEDGE.QUESTION_PLACEHOLDER')"
                />
              </label>
              <label class="grid gap-1">
                <span class="text-sm font-medium">{{
                  t('AI_AGENTS.KNOWLEDGE.ANSWER')
                }}</span>
                <textarea
                  v-model="item.answer"
                  rows="4"
                  maxlength="600"
                  class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                  :placeholder="t('AI_AGENTS.KNOWLEDGE.ANSWER_PLACEHOLDER')"
                />
                <span class="text-xs text-right text-n-slate-10">
                  {{ item.answer.length }}/600
                </span>
              </label>
            </div>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium rounded-lg border border-n-slate-6"
              @click="addFaq"
            >
              {{ t('AI_AGENTS.KNOWLEDGE.ADD_QUESTION') }}
            </button>
          </div>

          <label
            v-else-if="newDocument.source_type === 'text'"
            class="grid gap-1"
          >
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.CONTENT')
            }}</span>
            <textarea
              v-model="newDocument.content"
              rows="5"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>

          <div v-if="newDocument.source_type === 'industry'" class="grid gap-4">
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION')
              }}</span>
              <input
                v-model="industryForm.specialization"
                type="text"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.KNOWLEDGE.SPECIALIZATION_PLACEHOLDER')
                "
              />
            </label>
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME')
              }}</span>
              <input
                v-model="industryForm.businessName"
                type="text"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_NAME_PLACEHOLDER')
                "
              />
            </label>
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE')
              }}</span>
              <input
                v-model="industryForm.website"
                type="url"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_WEBSITE_PLACEHOLDER')
                "
              />
            </label>
            <label class="grid gap-1">
              <span class="text-sm font-medium">{{
                t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION')
              }}</span>
              <textarea
                v-model="industryForm.description"
                rows="4"
                class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
                :placeholder="
                  t('AI_AGENTS.KNOWLEDGE.BUSINESS_DESCRIPTION_PLACEHOLDER')
                "
              />
            </label>
          </div>

          <label v-if="isUrlType(newDocument.source_type)" class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.URL')
            }}</span>
            <input
              v-model="newDocument.source_url"
              type="url"
              class="px-3 py-2 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2"
            />
          </label>

          <label v-if="isFileType(newDocument.source_type)" class="grid gap-1">
            <span class="text-sm font-medium">{{
              t('AI_AGENTS.KNOWLEDGE.SELECT_FILE')
            }}</span>
            <div
              class="cursor-pointer flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed text-sm text-n-slate-10 border-n-slate-6 bg-n-surface-2 transition-colors hover:border-n-brand"
              :class="{ 'border-n-brand bg-n-brand/10': isDragging }"
              @dragenter.prevent
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="onDrop"
              @click="openFileInput"
            >
              <span class="i-lucide-upload-cloud w-8 h-8" />
              <span>{{ t('AI_AGENTS.KNOWLEDGE.DROP_TITLE') }}</span>
              <span class="text-xs">{{
                t('AI_AGENTS.KNOWLEDGE.DROP_SUBTITLE')
              }}</span>
            </div>
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.docx,.xlsx"
              class="hidden"
              @change="onFileChange"
            />
            <span v-if="selectedFile" class="text-xs text-n-slate-10">
              {{ selectedFile.name }}
            </span>
            <span v-if="fileError" class="text-xs text-n-ruby-9">
              {{ fileError }}
            </span>
          </label>

          <div class="flex justify-end gap-2">
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg border border-n-slate-6"
              @click="closeForm"
            >
              {{ t('AI_AGENTS.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
              @click="saveDocument"
            >
              {{ saveLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
